/**
 * "Full is cheaper than what you are about to build out of add-ons."
 *
 * A Starter firm that has bought two add-ons and is now blocked on a third is
 * about to spend more than a Full seat costs. Saying so at the moment they hit
 * the wall is the single most useful thing the lock screen can do - for them,
 * because they stop paying more for less, and for us, because Full is the
 * better place for a firm using three modules to be.
 *
 * PURE, AND ITS OWN MODULE, because it used to live inside `LicenseOverlay` as
 * a `useMemo` - which meant the upgrade MODAL, shown for exactly the same
 * situation from an action gate or a 403, could not offer it. One calculation,
 * both surfaces, and the arithmetic testable without rendering either.
 */

import { LicensePlan, OrgLicenseAccess } from '../../../store/actions/PlanActions';

export interface FullUpsell {
  fullPlan: LicensePlan;
  /** Monthly, per seat, in whatever unit the catalogue rows carry. */
  fullPrice: number;
  /** Starter + the add-ons they hold + the one they are being blocked on. */
  projectedTotal: number;
  /** Never negative - a near-miss is still worth showing, at zero. */
  savings: number;
  activeAddonCount: number;
}

/**
 * Within €10 still counts.
 *
 * A firm one add-on away from parity should be told, because the next module
 * they add tips it and because Full carries the whole pooled allowance rather
 * than three separate meters. Recommending it at break-even is honest; the
 * saving shown is clamped at zero so the card never advertises a negative.
 */
export const UPSELL_NEAR_MISS_TOLERANCE = 10;

/**
 * Is upgrading to Full cheaper than adding this add-on to Starter?
 *
 * Returns null - meaning "say nothing" - whenever the comparison would not be
 * honest: not an admin (they cannot act on it), not on Starter (there is
 * nothing to compare), no add-on in question, or the catalogue rows are not
 * loaded yet.
 */
export function computeFullUpsell(params: {
  isOrgAdmin: boolean;
  matchingAddon: LicensePlan | null;
  access: OrgLicenseAccess | null | undefined;
  plans: LicensePlan[];
  addons: LicensePlan[];
}): FullUpsell | null {
  const { isOrgAdmin, matchingAddon, access, plans, addons } = params;
  if (!isOrgAdmin || !matchingAddon) return null;

  const currentPlan = access?.license_info;
  if (!currentPlan || currentPlan.plan_type !== 'base') return null;
  if (currentPlan.plan_name !== 'pons_starter') return null;

  const fullPlan = plans.find((p) => p.name === 'pons_full');
  const starterPlan = plans.find((p) => p.name === 'pons_starter');
  if (!fullPlan || !starterPlan) return null;

  const activeAddonLicenseIds = new Set(
    (access?.licenses ?? [])
      .filter((l) => l.type === 'addon' && l.is_active)
      .map((l) => l.license_id)
  );

  const currentAddonSpend = addons
    .filter((addon) => activeAddonLicenseIds.has(addon.id))
    .reduce((total, addon) => total + addon.price_monthly, 0);

  const projectedTotal = starterPlan.price_monthly + currentAddonSpend + matchingAddon.price_monthly;
  const savings = projectedTotal - fullPlan.price_monthly;

  if (savings < -UPSELL_NEAR_MISS_TOLERANCE) return null;

  return {
    fullPlan,
    fullPrice: fullPlan.price_monthly,
    projectedTotal,
    savings: Math.max(0, savings),
    activeAddonCount: activeAddonLicenseIds.size,
  };
}

/** The add-on in the catalogue that unlocks a given section. */
export const addonForSection = (
  section: string | null | undefined,
  addons: LicensePlan[]
): LicensePlan | null =>
  (section && addons.find((a) => a.feature_gates?.sections?.includes(section))) || null;

/** …or a given AI feature. Gates block on both, so both have to resolve. */
export const addonForAiFeature = (
  feature: string | null | undefined,
  addons: LicensePlan[]
): LicensePlan | null =>
  (feature && addons.find((a) => a.feature_gates?.ai_features?.includes(feature))) || null;

/** Whichever of the two the gate was about. */
export const addonForKey = (
  key: string | null | undefined,
  addons: LicensePlan[]
): LicensePlan | null => addonForSection(key, addons) || addonForAiFeature(key, addons);
